// utils/animateMedia.js
import React from "react";
import { AnimatePresence, motion } from "framer-motion";

const VALID = {
    FADE: ["CENTER"],
    DISSOLVE: ["CENTER"],
    SLIDE: ["LEFT", "RIGHT", "UP", "DOWN"],
    FLIP: ["HORIZONTAL", "VERTICAL"],
    ZOOM: ["CENTER", "LEFT", "RIGHT", "UP", "DOWN"],
    ROLL: ["LEFT", "RIGHT", "UP", "DOWN"],
};

function clampDirection(type, dir) {
    const opts = VALID[type] || [];
    if (!opts.length) return "CENTER";
    return dir && opts.includes(dir) ? dir : opts[0];
}

export function getAnimationVariants(type = "FADE", direction = "CENTER", durationMs = 600) {
    const dur = Math.max(60, durationMs) / 1000;
    const ease = [0.22, 1, 0.36, 1];
    const dirSafe = clampDirection(type, direction);

    if (type === "FADE") {
        return {
            initial: { opacity: 0 },
            animate: { opacity: 1, transition: { duration: dur, ease } },
            exit: { opacity: 0, transition: { duration: dur, ease } },
        };
    }

    if (type === "DISSOLVE") {
        return {
            initial: { opacity: 0 },
            animate: { opacity: 1, transition: { duration: dur, ease: "easeInOut" } },
            exit: { opacity: 0, transition: { duration: dur, ease: "easeInOut" } },
        };
    }

    if (type === "SLIDE") {
        const dx = 60, dy = 40;
        const from =
            dirSafe === "LEFT" ? { x: -dx, y: 0 } :
                dirSafe === "RIGHT" ? { x: dx, y: 0 } :
                    dirSafe === "UP" ? { x: 0, y: -dy } :
                        { x: 0, y: dy }; // DOWN
        return {
            initial: { opacity: 0, ...from },
            animate: { opacity: 1, x: 0, y: 0, transition: { duration: dur, ease } },
            exit: { opacity: 0, ...from, transition: { duration: dur, ease } },
        };
    }

    if (type === "FLIP") {
        const axis = dirSafe === "VERTICAL" ? "rotateX" : "rotateY";
        return {
            initial: { opacity: 0.2, [axis]: -90 },
            animate: { opacity: 1, [axis]: 0, transition: { duration: dur, ease } },
            exit: { opacity: 0.2, [axis]: 90, transition: { duration: dur, ease } },
        };
    }

    if (type === "ZOOM") {
        const origin =
            dirSafe === "LEFT" ? "0% 50%" :
                dirSafe === "RIGHT" ? "100% 50%" :
                    dirSafe === "UP" ? "50% 0%" :
                        dirSafe === "DOWN" ? "50% 100%" :
                            "50% 50%";
        return {
            initial: { opacity: 0, scale: 0.85, transformOrigin: origin },
            animate: { opacity: 1, scale: 1, transformOrigin: origin, transition: { duration: dur, ease } },
            exit: { opacity: 0, scale: 0.6, transformOrigin: origin, transition: { duration: dur, ease } },
        };
    }

    // ROLL
    const distX = 140, distY = 100, spin = 120;
    const map = {
        LEFT: { from: { x: -distX, y: 0 }, exit: { x: distX, y: 0 }, spinIn: spin, spinOut: spin },
        RIGHT: { from: { x: distX, y: 0 }, exit: { x: -distX, y: 0 }, spinIn: -spin, spinOut: -spin },
        UP: { from: { x: 0, y: -distY }, exit: { x: 0, y: distY }, spinIn: -spin, spinOut: -spin },
        DOWN: { from: { x: 0, y: distY }, exit: { x: 0, y: -distY }, spinIn: spin, spinOut: spin },
    }[(dirSafe || "LEFT")] || { from: { x: -distX, y: 0 }, exit: { x: distX, y: 0 }, spinIn: spin, spinOut: spin };

    return {
        initial: { opacity: 0.1, x: map.from.x, y: map.from.y, rotateZ: map.spinIn },
        animate: { opacity: 1, x: 0, y: 0, rotateZ: 0, transition: { duration: dur * 0.5, ease } },
        exit: { opacity: 0.01, x: map.exit.x, y: map.exit.y, rotateZ: map.spinOut, transition: { duration: dur * 0.85, ease: [0.4, 0, 0.6, 1] } },
    };
}

export default function AnimatedWrap({
    itemKey,
    type = "FADE",
    direction = "CENTER",
    durationMs = 600,
    style,
    className,
    children,
}) {
    const variants = getAnimationVariants(type, direction, durationMs);
    return (
        <div className={className} style={{ perspective: 1000, ...style }}>
            <AnimatePresence mode="wait">
                <motion.div
                    key={itemKey}
                    className="w-full h-full"
                    initial={variants.initial}
                    animate={variants.animate}
                    exit={variants.exit}
                >
                    {children}
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
